import { useEffect, useRef, useState } from 'react'
import { api, ErroreApi } from '../servizi/api.ts'
import { useCatalogo, usePaesi } from '../servizi/sessione.tsx'
import { pesoLeggibile } from '../servizi/regole-foto.ts'
import type { StatiScheda } from '../servizi/regole-anagrafica.ts'
import {
  applicaLettura, origineLeggibile, scelteIniziali, vociDaLettura,
  type ChiaveVoce, type LetturaProposta, type NomeDi, type Proposta,
} from '../servizi/regole-lettura-documento.ts'
import { Bottone, Etichetta, Riquadro, Spunta, type TonoEtichetta } from './base.tsx'
import { Avviso } from './Pezzi.tsx'
import { IconaDocumento, IconaImmagine } from './Icone.tsx'
import { riduciFoto } from './FotoDocumento.tsx'

/** Il file scelto: l'originale, da allegare dopo il salvataggio, e quello che parte per la lettura. */
type FileScelto = { originale: File; invio: Blob; pdf: boolean; anteprima: string | null }

type Fase = 'scelta' | 'preparazione' | 'pronto' | 'lettura' | 'risultato' | 'usato'

/** Stessa soglia dell'API (servizi/lettura-documento.ts, DIMENSIONE_MASSIMA). */
const MASSIMO_PREDEFINITO = 8 * 1024 * 1024

/** Base64 di un file, senza l'intestazione del data URL, a pezzi: String.fromCharCode non regge 8 MB in una volta. */
async function base64(dati: Blob): Promise<string> {
  const byte = new Uint8Array(await dati.arrayBuffer())
  let binario = ''
  for (let i = 0; i < byte.length; i += 0x8000) binario += String.fromCharCode(...byte.subarray(i, i + 0x8000))
  return btoa(binario)
}

/** Per la riga con piu' dati (paese e luogo di nascita) l'origine da mostrare e' quella meno sicura. */
const menoSicura = (proposte: Proposta[]): Proposta =>
  proposte.reduce((a, b) => ((b.confidenza ?? 1) < (a.confidenza ?? 1) ? b : a))

const tonoDi = (p: Proposta): TonoEtichetta =>
  p.origine === 'mrz' ? 'positivo' : p.origine === 'codice_fiscale' ? 'informativo'
    : p.confidenza !== null && p.confidenza < 0.7 ? 'attenzione' : 'neutro'

const NOMI_TIPO: Record<LetturaProposta['tipo_letto'], string> = {
  passaporto: 'Passaporto', carta_identita: 'Carta d’identità', permesso_soggiorno: 'Permesso di soggiorno',
  tessera_sanitaria: 'Tessera sanitaria', patente: 'Patente', altro: 'Documento',
}

/**
 * «Compila dal documento» (lettura con l'AI, 31/10/2026), in cima alla nuova scheda.
 *
 * Si sceglie un file o si scatta una foto; le foto si riducono nel browser come la foto del
 * documento (FotoDocumento, D-03: 1600 px, JPEG, senza la posizione dello scatto) prima di partire, i
 * PDF partono come sono. L'API li manda al modello e restituisce i dati gia' nei codici del
 * gestionale; qui la tabella «Dal documento → Nella scheda» li mette accanto al modulo, con una
 * casella per riga. «Usa i dati scelti» riempie il modulo: niente si salva da solo, e i campi
 * riempiti restano evidenziati finche' l'operatore non li tocca.
 *
 * Con la funzione spenta (GET /persone/lettura-documento dice `attiva: false`) il riquadro non c'e'.
 * Con «Dati anonimizzati» spuntato la lettura non serve: dice perche'.
 */
export function CompilaDalDocumento({ stati, conCondizione, anonima, usa }: {
  stati: StatiScheda
  /** Il modulo compila la condizione giuridica: senza, le sue righe non compaiono. */
  conCondizione: boolean
  anonima: boolean
  /** «Usa i dati scelti»: il modulo riempito, i campi da evidenziare, il file e il tipo di documento letto. */
  usa: (esito: { stati: StatiScheda; letti: string[] }, documento: { file: File; tipo: string | null }) => void
}) {
  const [attiva, setAttiva] = useState<{ massimo: number } | null>(null)
  const [fase, setFase] = useState<Fase>('scelta')
  const [file, setFile] = useState<FileScelto | null>(null)
  const [lettura, setLettura] = useState<LetturaProposta | null>(null)
  const [scelte, setScelte] = useState<Set<ChiaveVoce>>(new Set())
  const [errore, setErrore] = useState<string | null>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const sceltaFile = useRef<HTMLInputElement>(null)
  const fotocamera = useRef<HTMLInputElement>(null)
  const titoloRisultato = useRef<HTMLHeadingElement>(null)

  const generi = useCatalogo('genere')
  const condizioni = useCatalogo('condizione_giuridica')
  const motivi = useCatalogo('motivo_permesso')
  const paesi = usePaesi()
  const nomeDi: NomeDi = (catalogo, codice) => (
    catalogo === 'paese' ? paesi.nomeDi(codice) : catalogo === 'genere' ? generi.nomeDi(codice)
      : catalogo === 'condizione_giuridica' ? condizioni.nomeDi(codice) : motivi.nomeDi(codice))

  useEffect(() => {
    let vivo = true
    api.get<{ attiva: boolean; dimensione_massima?: number }>('/persone/lettura-documento')
      .then((r) => { if (vivo) setAttiva(r.attiva ? { massimo: r.dimensione_massima ?? MASSIMO_PREDEFINITO } : null) })
      // Senza risposta il riquadro non compare: il modulo si compila a mano, come prima.
      .catch(() => { if (vivo) setAttiva(null) })
    return () => { vivo = false }
  }, [])

  // L'anteprima tiene in memoria la foto: si rilascia quando cambia o si esce.
  useEffect(() => () => { if (file?.anteprima) URL.revokeObjectURL(file.anteprima) }, [file])

  // Letto il documento, il fuoco va sul titolo del risultato: chi usa un lettore di schermo sente l'esito.
  useEffect(() => { if (fase === 'risultato') titoloRisultato.current?.focus() }, [fase])

  if (!attiva) return null

  // Le righe si ricalcolano a ogni disegno: «Nella scheda» segue il modulo anche se lo si cambia con la
  // tabella aperta. Le caselle scelte invece restano quelle dell'operatore.
  const voci = lettura ? vociDaLettura(lettura, stati, nomeDi, { conCondizione }) : []

  if (anonima) {
    return (
      <Riquadro titolo="Compila dal documento">
        <p className="persona-nota-sezione">
          Con «Dati anonimizzati» la lettura del documento non è disponibile: la scheda non registra nome intero, data
          di nascita, luogo di nascita e codici, cioè proprio i dati che si leggono dal documento.
        </p>
      </Riquadro>
    )
  }

  function ricomincia() {
    setFile(null); setLettura(null); setScelte(new Set()); setErrore(null); setEsito(null); setFase('scelta')
  }

  async function scelto(f: File | undefined) {
    if (!f) return
    setErrore(null); setEsito(null); setLettura(null)
    const pdf = f.type === 'application/pdf' || /\.pdf$/i.test(f.name)
    if (pdf) {
      if (f.size > attiva!.massimo) {
        setErrore(`Il PDF supera gli ${Math.round(attiva!.massimo / (1024 * 1024))} MB: fotografa il documento, oppure `
          + 'scansionalo con una risoluzione più bassa.')
        return
      }
      setFile({ originale: f, invio: f, pdf: true, anteprima: null })
      setFase('pronto')
      return
    }
    setFase('preparazione')
    try {
      // Come la foto del documento: piu' leggera da mandare, e senza i dati dello scatto.
      const ridotta = await riduciFoto(f)
      setFile({ originale: f, invio: ridotta.dati, pdf: false, anteprima: URL.createObjectURL(ridotta.dati) })
      setFase('pronto')
    } catch {
      setFile(null); setFase('scelta')
      setErrore('La foto non si può leggere in questo browser. Riprova con la fotocamera, oppure con un’immagine JPEG o '
        + 'PNG o con un PDF.')
    }
  }

  async function leggi() {
    if (!file) return
    setErrore(null); setFase('lettura')
    try {
      const risposta = await api.post<LetturaProposta>('/persone/lettura-documento', {
        nome_file: file.originale.name.slice(0, 200), contenuto: await base64(file.invio),
      })
      setLettura(risposta)
      setScelte(scelteIniziali(vociDaLettura(risposta, stati, nomeDi, { conCondizione })))
      setFase('risultato')
    } catch (e) {
      setFase('pronto')
      setErrore(e instanceof ErroreApi ? e.message : 'La lettura non è riuscita: controlla la connessione e riprova.')
    }
  }

  function usaScelti() {
    if (!lettura || !file) return
    const esitoModulo = applicaLettura(stati, lettura, scelte)
    usa(esitoModulo, { file: file.originale, tipo: lettura.campi.tipo_documento?.valore ?? null })
    setEsito(`${esitoModulo.letti.length === 1 ? 'Un campo riempito' : `${esitoModulo.letti.length} campi riempiti`} `
      + 'dal documento: sono evidenziati nel modulo. Controllali sul documento prima di salvare.')
    setFase('usato')
  }

  const cambiaScelta = (chiave: ChiaveVoce, si: boolean) => setScelte((s) => {
    const nuove = new Set(s)
    if (si) nuove.add(chiave); else nuove.delete(chiave)
    return nuove
  })

  const doc = lettura?.campi
  const occupato = fase === 'preparazione' || fase === 'lettura'

  return (
    <Riquadro titolo="Compila dal documento">
      <p className="persona-nota-sezione">
        Carica la foto o il PDF scansionato del documento d’identità: il gestionale ne legge i dati e te li propone qui
        sotto. Il documento passa per un servizio dei server di Honey et Molly a Parigi, che non lo conserva; qui non si salva niente
        finché non salvi la scheda. Controlla sempre i dati letti.
      </p>

      {/* Due campi file: con `capture` il telefono apre subito la fotocamera e non lascia scegliere un PDF. */}
      <input ref={sceltaFile} type="file" accept="image/*,application/pdf" hidden
             onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; void scelto(f) }} />
      <input ref={fotocamera} type="file" accept="image/*" capture="environment" hidden
             onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; void scelto(f) }} />

      {(fase === 'scelta' || fase === 'preparazione' || fase === 'pronto' || fase === 'lettura') && (
        <div className="lettura-comandi">
          <Bottone variante="secondario" icona={<IconaImmagine />} disabled={occupato}
                   onClick={() => fotocamera.current?.click()}>
            Scatta una foto
          </Bottone>
          <Bottone variante="secondario" icona={<IconaDocumento />} disabled={occupato}
                   inCorso={fase === 'preparazione'} onClick={() => sceltaFile.current?.click()}>
            {fase === 'preparazione' ? 'Preparazione della foto…' : file ? 'Scegli un altro file' : 'Scegli un file'}
          </Bottone>
        </div>
      )}

      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <div role="status">{esito && <Avviso tipo="successo">{esito}</Avviso>}</div>

      {file && (fase === 'pronto' || fase === 'lettura') && (
        <>
          <figure className="persona-foto-anteprima">
            {file.anteprima
              ? <img src={file.anteprima} alt="Anteprima del documento da leggere" />
              : <p className="lettura-pdf"><IconaDocumento /> PDF, {pesoLeggibile(file.invio.size)}</p>}
            <figcaption className="piccolo tenue">
              {file.pdf
                ? 'Dal PDF si leggono le scansioni delle prime due pagine.'
                : 'La foto è ridotta e senza la posizione dello scatto. Il documento deve essere intero, a fuoco e senza riflessi.'}
            </figcaption>
          </figure>
          <div className="lettura-comandi">
            <Bottone variante="primario" inCorso={fase === 'lettura'} onClick={() => void leggi()}>
              {fase === 'lettura' ? 'Lettura in corso…' : 'Leggi il documento'}
            </Bottone>
            <Bottone variante="discreto" disabled={fase === 'lettura'} onClick={ricomincia}>Annulla</Bottone>
          </div>
        </>
      )}

      {lettura && fase === 'risultato' && (
        <div className="lettura-risultato">
          <h3 ref={titoloRisultato} tabIndex={-1}>
            {NOMI_TIPO[lettura.tipo_letto]}: dal documento → nella scheda
          </h3>
          {lettura.avvisi.length > 0 && (
            <Avviso tipo="attenzione">
              <ul className="elenco-avviso">{lettura.avvisi.map((a) => <li key={a}>{a}</li>)}</ul>
            </Avviso>
          )}
          {voci.length === 0 ? (
            <p className="tenue">Nessun dato da riportare nel modulo.</p>
          ) : (
            <table className="lettura-tabella">
              <caption className="solo-lettore">Dati letti dal documento e valori nel modulo</caption>
              <thead>
                <tr><th scope="col">Dato</th><th scope="col">Dal documento</th><th scope="col">Nella scheda</th></tr>
              </thead>
              <tbody>
                {voci.map((v) => (
                  <tr key={v.chiave}>
                    <td className="lettura-scelta" data-etichetta="">
                      <Spunta etichetta={v.etichetta} valore={scelte.has(v.chiave)}
                              cambia={(si) => cambiaScelta(v.chiave, si)} />
                    </td>
                    <td data-etichetta="Dal documento">
                      <span className="lettura-valore">{v.dalDocumento}</span>{' '}
                      <Etichetta tono={tonoDi(menoSicura(v.proposte))}>{origineLeggibile(menoSicura(v.proposte))}</Etichetta>
                      {v.proposte.flatMap((p) => p.avvisi).map((a) => (
                        <p key={a} className="lettura-avviso piccolo">{a}</p>
                      ))}
                    </td>
                    <td data-etichetta="Nella scheda">
                      {v.uguale ? <span className="tenue">uguale</span>
                        : v.nellaScheda ? <span className="lettura-valore">{v.nellaScheda}</span>
                          : <span className="tenue">vuoto</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="piccolo tenue">
            {[doc?.tipo_documento?.testo && `Tipo: ${doc.tipo_documento.testo}`,
              doc?.numero_documento && `numero ${doc.numero_documento.testo}`,
              lettura.documento.data_scadenza && `scade il ${lettura.documento.data_scadenza.split('-').reverse().join('/')}`]
              .filter(Boolean).join(', ')}
            {doc?.tipo_documento || doc?.numero_documento ? '. ' : ''}
            Il modulo non ha i campi del documento: dopo il salvataggio puoi allegare questo file alla scheda.
          </p>
          <div className="lettura-comandi">
            <Bottone variante="primario" disabled={scelte.size === 0} onClick={usaScelti}>Usa i dati scelti</Bottone>
            <Bottone variante="discreto" onClick={ricomincia}>Annulla</Bottone>
          </div>
        </div>
      )}

      {fase === 'usato' && (
        <div className="lettura-comandi">
          <Bottone variante="secondario" onClick={ricomincia}>Leggi un altro documento</Bottone>
        </div>
      )}
    </Riquadro>
  )
}
